/// <amd-module name="Feed/Admin/Services/GlobalSettingsService" />
import { IdentitiesSearchRequestModel, IIdentity } from "VSS/Features/Identities/IdentityService";
import { IIdentitiesRestClient } from "VSS/Features/Identities/RestClient/Identities";
import { IVssPageContext, IVssRestClientOptions, Services, VssService } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

import { GlobalPermission, GlobalRole } from "Feed/Common/Contracts/Feed";
import * as Resources from "Feed/Common/Resources";
import { getFeedClient, IFeedRestClient } from "Feed/Common/RestClient/Feed";
import { IFeedWebPageDataService } from "Feed/Common/Services/IFeedWebPageDataService";

import { NormalizedGlobalPermission } from "../NormalizedGlobalPermission";
import { IFeedGlobalSettingsService, INormalizedGlobalPermission, IRoleAssignment } from "../Types";

export class FeedGlobalSettingsService extends VssService implements IFeedGlobalSettingsService {
    private _feedRestClient: IFeedRestClient;
    private _identityRestClient: IIdentitiesRestClient;
    private _feedWebPageDataService: IFeedWebPageDataService;

    private _pcaId: string;
    private _everyoneId: string;
    private _tfidToDescriptor = new Map<string, string>();
    private _tfidToIIdentity = new Map<string, IIdentity>();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._feedRestClient = getFeedClient(pageContext);
        this._identityRestClient = this.pageContext.getRestClient<IIdentitiesRestClient>("IIdentitiesRestClient", {
            serviceInstanceType: "951917ac-a960-4999-8464-e3f0aa25b381" // SPS
        } as IVssRestClientOptions);

        this._feedWebPageDataService = this.pageContext.getService<IFeedWebPageDataService>("IFeedWebPageDataService");
    }

    public getProjectCollectionAdminId(): string {
        if (this._pcaId == null) {
            // this value is null if the user is public/anonymous, but those users don't have
            // access to global settings
            this._pcaId = this._feedWebPageDataService.getProjectCollectionAdminGroupId();
        }
        return this._pcaId;
    }

    public getEveryoneGroupId(): string {
        if (this._everyoneId == null) {
            this._everyoneId = this._feedWebPageDataService.getEveryoneGroupId();
        }
        return this._everyoneId;
    }

    private loadPermissions(): Promise<GlobalPermission[]> {
        return this._feedRestClient.getGlobalPermissions(true);
    }

    public async getPermissionDataAsync(): Promise<INormalizedGlobalPermission[]> {
        const permissions = await this.loadPermissions();
        const pcaIdentity = this.getProjectCollectionAdminId();

        // Don't care one way or the other about the permissions of the Administrators group or identities with no permissions.
        const normalizedPermissions = permissions
            .map(NormalizedGlobalPermission.fromGlobalPermission)
            .filter(x => x.identityId !== pcaIdentity && x.role != GlobalRole.None);

        return normalizedPermissions;
    }

    public async savePermissionDataAsync(modifiedRoles: IRoleAssignment[]): Promise<void> {
        const modifiedRoleTfids = modifiedRoles.map(x => x.tfid);
        await this.cacheIdentitiesByTfidAsync(modifiedRoleTfids);
        if (modifiedRoleTfids.some(x => !this._tfidToDescriptor.has(x))) {
            throw new Error(Resources.GlobalSettingsDialogPermissions_ReadIdentitiesError);
        }

        const globalPermissions = modifiedRoles.map(x =>
            new NormalizedGlobalPermission(this._tfidToDescriptor.get(x.tfid)!, x.role, x.tfid).toGlobalPermission()
        );

        await this._feedRestClient.setGlobalPermissions(globalPermissions);
    }

    public async getIIdentityFromPermissionAsync(
        permissions: INormalizedGlobalPermission[]
    ): Promise<Map<string, IIdentity>> {
        const missing = permissions.filter(permission => !this._tfidToDescriptor.has(permission.identityId));

        if (missing.length > 0) {
            const missingIdentities: string[] = [];
            for (const permission of missing) {
                this._tfidToDescriptor.set(permission.identityId, permission.identityDescriptor);
                missingIdentities.push(permission.identityId);
            }
            await this.cacheIIdentities(missingIdentities);
        }

        const idToIIdentity = new Map<string, IIdentity>();

        for (const permission of permissions) {
            const iidentity = this._tfidToIIdentity.get(permission.identityId);
            if (iidentity != null) {
                idToIIdentity.set(permission.identityId, iidentity);
            }
        }
        return idToIIdentity;
    }

    private async cacheIdentitiesByTfidAsync(tfids: string[]): Promise<void> {
        const missing = tfids.filter(x => !this._tfidToDescriptor.has(x));

        if (missing.length === 0) {
            return;
        }

        return this.cacheIdentities(await this._identityRestClient.readIdentities(undefined, missing.join()));
    }

    private async cacheIdentities(identities: Identity[]): Promise<void> {
        const validIdentities: string[] = [];
        for (const identity of identities) {
            if (identity == null) {
                continue;
            }

            validIdentities.push(identity.id);

            const descriptor = NormalizedGlobalPermission.normalizeIdentityDescriptorToString(identity.descriptor);
            this._tfidToDescriptor.set(identity.id, descriptor);
        }

        return this.cacheIIdentities(validIdentities);
    }

    // takes an array of Identity ids and populates this._tfidToIIdentity
    private async cacheIIdentities(tfids: string[]): Promise<void> {
        const identityQuery = tfids.join(";");

        // IdentityService doesn't support multiple queries, so use IIdentityPickerHttpClient directly
        // but it's not currently exported :/ so 'any' it is...
        // import { IIdentityPickerHttpClient } from "VSS/Features/Identities/IdentityService";
        const identityPickerClient = this.pageContext.getRestClient<any>("IIdentityPickerHttpClient");

        const requestProps: IdentitiesSearchRequestModel = {
            query: identityQuery,
            identityTypes: ["user", "group"],
            operationScopes: ["ims"],
            queryTypeHint: "uid",
            options: {
                MinResults: 5,
                MaxResults: 40
            },
            properties: ["DisplayName", "IsMru", "ScopeName", "SamAccountName", "Active", "SubjectDescriptor"]
        };

        const identityResults = await identityPickerClient.beginGetIdentities(requestProps);
        if (identityResults != null && identityResults.results != null) {
            for (const result of identityResults.results) {
                for (const identity of result.identities) {
                    if (identity == null || identity.localId == null) {
                        continue;
                    }

                    this._tfidToIIdentity.set(identity.localId, identity);
                }
            }
        }
    }
}

Services.add("IFeedGlobalSettingsService", { serviceFactory: FeedGlobalSettingsService });
